import React, { useState, useEffect } from 'react'
// import { Toaster } from 'react-hot-toast'
import JSONInput from './components/JSONInput-simple'
import JSONViewer from './components/JSONViewer-simple'
import Toolbar from './components/Toolbar-simple'
import ConversionPanel from './components/ConversionPanel'
import StatsPanel from './components/StatsPanel'
import { validateJSON, formatJSON, minifyJSON } from './utils/jsonUtils'
import { downloadFile, copyToClipboard } from './utils/fileUtils'
import './App.css'

function App() {
  const [jsonInput, setJsonInput] = useState('')
  const [parsedJson, setParsedJson] = useState(null)
  const [isValid, setIsValid] = useState(true)
  const [error, setError] = useState('')
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('format') // 'format', 'parse', 'converter', 'stats'

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark')
      document.documentElement.setAttribute('data-theme', savedTheme)
    }
  }, [])

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newTheme = !isDarkMode
    setIsDarkMode(newTheme)
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light')
    localStorage.setItem('theme', newTheme ? 'dark' : 'light')
  }

  // Validate JSON input
  const handleInputChange = (value) => {
    setJsonInput(value)
    setError('')
    
    if (value.trim() === '') {
      setParsedJson(null)
      setIsValid(true)
      return
    }

    try {
      const parsed = JSON.parse(value)
      setParsedJson(parsed)
      setIsValid(true)
      setError('')
    } catch (err) {
      setIsValid(false)
      setError(err.message)
      setParsedJson(null)
    }
  }

  // Format JSON
  const handleFormat = () => {
    if (!isValid || !parsedJson) return
    
    try {
      const formatted = formatJSON(parsedJson)
      setJsonInput(formatted)
    } catch (err) {
      setError('Failed to format JSON: ' + err.message)
    }
  }

  // Minify JSON
  const handleMinify = () => {
    if (!isValid || !parsedJson) return
    
    try {
      const minified = minifyJSON(parsedJson)
      setJsonInput(minified)
    } catch (err) {
      setError('Failed to minify JSON: ' + err.message)
    }
  }

  // Validate JSON
  const handleValidate = () => {
    if (jsonInput.trim() === '') {
      setError('Please enter JSON to validate')
      return
    }
    
    const validation = validateJSON(jsonInput)
    if (validation.isValid) {
      setError('')
      setIsValid(true)
    } else {
      setError(validation.error)
      setIsValid(false)
    }
  }

  // Download JSON
  const handleDownload = () => {
    if (!isValid || !parsedJson) return
    
    const filename = 'json-data.json'
    const content = formatJSON(parsedJson)
    downloadFile(filename, content, 'application/json')
  }

  // Copy to clipboard
  const handleCopy = async () => {
    if (!isValid || !parsedJson) return
    
    const content = formatJSON(parsedJson)
    await copyToClipboard(content)
  }

  // Clear all
  const handleClear = () => {
    setJsonInput('')
    setParsedJson(null)
    setIsValid(true)
    setError('')
    setSearchTerm('')
  }

  // Handle YAML to JSON conversion
  const handleYamlToJson = (jsonData) => {
    try {
      const formatted = formatJSON(jsonData)
      setJsonInput(formatted)
      setParsedJson(jsonData)
      setIsValid(true)
      setError('')
    } catch (err) {
      setError('Failed to convert YAML to JSON: ' + err.message)
    }
  }

  return (
    <div className="container" data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'var(--secondary-bg)',
            color: 'var(--text-color)',
            border: '1px solid var(--border-color)',
          },
        }}
      /> */}
      
      <header className="header">
        <h1>JSON Parser Online</h1>
        <p>Validate, format, minify, and explore your JSON data with ease</p>
      </header>

      <Toolbar
        onFormat={handleFormat}
        onMinify={handleMinify}
        onValidate={handleValidate}
        onDownload={handleDownload}
        onCopy={handleCopy}
        onClear={handleClear}
        onToggleTheme={toggleDarkMode}
        isDarkMode={isDarkMode}
        isValid={isValid}
        hasContent={jsonInput.trim() !== ''}
      />

      <div className="main-content">
        <div className="panel">
          <div className="panel-header">
            <span>JSON Input</span>
            <div className="status-bar">
              <span className={isValid ? 'status-valid' : 'status-invalid'}>
                {isValid ? '✓ Valid JSON' : '✗ Invalid JSON'}
              </span>
            </div>
          </div>
          <div className="panel-content">
            <JSONInput
              value={jsonInput}
              onChange={handleInputChange}
              isDarkMode={isDarkMode}
              error={error}
            />
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className={`btn ${activeTab === 'format' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('format')}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  Format
                </button>
                <button
                  className={`btn ${activeTab === 'parse' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('parse')}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  Parse
                </button>
                <button
                  className={`btn ${activeTab === 'converter' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('converter')}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  JSON ⇄ YAML Converter
                </button>
                <button
                  className={`btn ${activeTab === 'stats' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setActiveTab('stats')}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  Stats
                </button>
              </div>
            </div>
            {(activeTab === 'format' || activeTab === 'parse') && (
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search in JSON..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="btn btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="panel-content">
            {activeTab === 'format' && (
              <JSONViewer
                data={parsedJson}
                searchTerm={searchTerm}
                isDarkMode={isDarkMode}
                isValid={isValid}
                viewMode="format"
              />
            )}
            {activeTab === 'parse' && (
              <JSONViewer
                data={parsedJson}
                searchTerm={searchTerm}
                isDarkMode={isDarkMode}
                isValid={isValid}
                viewMode="parse"
              />
            )}
            {activeTab === 'converter' && (
              <ConversionPanel
                jsonData={parsedJson}
                onYamlToJson={handleYamlToJson}
              />
            )}
            {activeTab === 'stats' && (
              <StatsPanel
                jsonData={parsedJson}
                jsonString={jsonInput}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default App
